import { FC, useMemo } from 'react'
import { useFormik } from 'formik'

// Imports diretos (e não pelos barrels `_metronic/helpers` e `app/components`): o barril de
// componentes arrasta o app inteiro — calendário, editores, PDF — e torna este bloco impossível de
// testar isoladamente. Mesmo padrão já usado em `app/modules/brand-management`.
import { ButtonType } from '../../../../../../_metronic/helpers/enumsUI'
import { Button } from '../../../../../components/Button/Button'
import { FormSelect } from '../../../../../components/Form/FormSelect'
import { IOption } from '../../../../../components/Input/CustomSelect'
import { useModal } from '../../../../../components/Modal/ModalContext'
import {
  EsusCondutaView,
  EsusEncerramentoLotacaoView,
  EsusEncerramentoProcedimentoView,
} from '../../../atendimento/core/_models'
import { encerramentoEsusSchema } from './validation/encerramentoEsusSchema'

/** "Alta do episódio" — conduta pré-selecionada, mesmo default do backend. */
export const CODIGO_CONDUTA_ALTA_DO_EPISODIO = 9

export type EncerramentoEsusValores = {
  /** Id da lotação a carimbar; `undefined` quando o profissional não tem lotação ativa na unidade. */
  lotacao?: number
  /** Códigos do LEDI escolhidos — nunca vazio (o backend recusa lista vazia). */
  condutas: number[]
}

type Props = {
  lotacoes: EsusEncerramentoLotacaoView[]
  condutas: EsusCondutaView[]
  /**
   * Procedimentos derivados no backend das aferições do atendimento — **somente leitura**.
   *
   * Default `[]` de propósito: backend mais antigo não manda a chave, e o bloco simplesmente não
   * aparece. Nada aqui pode segurar o encerramento.
   */
  procedimentos?: EsusEncerramentoProcedimentoView[]
  onConfirm: (valores: EncerramentoEsusValores) => void
}

const rotuloLotacao = (lotacao: EsusEncerramentoLotacaoView): string => {
  const cbo = lotacao.cbo
  const base = cbo
    ? [cbo.codigo, cbo.descricao].filter(Boolean).join(' - ')
    : `Lotação ${lotacao.id}`
  return lotacao.ine ? `${base} (INE ${lotacao.ine})` : base
}

const paraOpcaoLotacao = (lotacao: EsusEncerramentoLotacaoView): IOption<number> => ({
  label: rotuloLotacao(lotacao),
  value: lotacao.id,
})

const paraOpcaoConduta = (conduta: EsusCondutaView): IOption<number> => ({
  label: conduta.descricao,
  value: conduta.codigo,
})

/**
 * Bloco e-SUS do encerramento do atendimento (E5c): lotação a carimbar e condutas do atendimento.
 *
 * Só é aberto quando a integração e-SUS está ativa na unidade do atendimento — unidade sem
 * integração não vê nada disso. Não há campo de turno nem de local de atendimento: os dois são
 * derivados no backend.
 *
 * O bloco "Procedimentos" (F3) é **somente leitura** e só aparece quando o backend manda a lista: ele
 * mostra o que a Ficha de Procedimentos vai levar em nome do profissional, sem nenhum campo editável e
 * sem tocar no payload do encerramento.
 *
 * O seletor de lotação segue as três situações decididas em E1/E3:
 * - mais de uma lotação ativa: seletor obrigatório, pré-selecionado na última usada (marcação vinda
 *   da API) ou, na falta dela, na primeira da lista;
 * - exatamente uma: sem seletor, a lotação vai no payload automaticamente;
 * - nenhuma: sem seletor e sem validação — o encerramento conclui e a pendência aparece depois na
 *   lista de envios.
 */
export const EncerramentoEsusModal: FC<Props> = ({
  lotacoes,
  condutas,
  procedimentos = [],
  onConfirm,
}) => {
  const { closeModal } = useModal()

  const exigeLotacao = lotacoes.length > 1

  // Sem procedimento não há bloco nenhum: nem título, nem moldura, nem "nenhum procedimento".
  const temProcedimentos = procedimentos.length > 0

  const opcoesLotacao = useMemo(() => lotacoes.map(paraOpcaoLotacao), [lotacoes])
  const opcoesConduta = useMemo(() => condutas.map(paraOpcaoConduta), [condutas])

  const lotacaoInicial = useMemo(() => {
    if (!exigeLotacao) return null
    const sugerida = lotacoes.find((lotacao) => lotacao.ultimaUsada) ?? lotacoes[0]
    return paraOpcaoLotacao(sugerida)
  }, [exigeLotacao, lotacoes])

  const condutasIniciais = useMemo(
    () => opcoesConduta.filter((opcao) => opcao.value === CODIGO_CONDUTA_ALTA_DO_EPISODIO),
    [opcoesConduta]
  )

  const formik = useFormik({
    initialValues: {
      lotacao: lotacaoInicial as IOption<number> | null,
      condutas: condutasIniciais as IOption<number>[],
    },
    validationSchema: encerramentoEsusSchema(exigeLotacao),
    onSubmit: (values) => {
      // Com uma única lotação o seletor não existe, mas o payload continua explícito.
      const lotacaoEscolhida = exigeLotacao
        ? (values.lotacao?.value as number | undefined)
        : lotacoes[0]?.id

      onConfirm({
        lotacao: lotacaoEscolhida ?? undefined,
        condutas: values.condutas.map((opcao) => Number(opcao.value)),
      })
      closeModal()
    },
  })

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      <p className='text-muted fs-7 mb-5'>
        {temProcedimentos
          ? 'Estas informações compõem a Ficha de Atendimento Individual e a Ficha de Procedimentos enviadas ao e-SUS APS.'
          : 'Estas informações compõem a Ficha de Atendimento Individual enviada ao e-SUS APS.'}
      </p>

      {exigeLotacao && (
        <FormSelect
          formik={formik}
          name='lotacao'
          label='Lotação'
          options={opcoesLotacao}
          placeholder='Selecione a lotação'
          className='mb-5'
          isRequired
        />
      )}

      <FormSelect
        formik={formik}
        name='condutas'
        label='Condutas'
        options={opcoesConduta}
        placeholder='Selecione as condutas'
        isMulti
        hideSelectAll
        isRequired
      />

      {/*
        Bloco somente leitura: nenhum campo do formik, nenhum controle. O payload do encerramento
        continua sendo `{ lotacao, condutas }` — escolher procedimento é decisão do dono e não existe.
        Só classes do tema (nenhuma cor nova, nada de `badge-light-primary`, ilegível desde a E5a).

        Contraste: nada aqui usa `text-muted`. Ele é `gray-500` e mede, sobre o fundo real da modal,
        2,22:1 no tema claro e 2,33:1 no escuro — reprova o AA de 4,5:1. É o mesmo achado de uso real
        de 2026-09-07 já registrado em `esus/cadastro/EsusCadastroPacienteModal.tsx`, com o mesmo
        remédio: `text-gray-700` (5,46:1 claro / 5,34:1 escuro) no texto auxiliar e no cabeçalho, e
        `text-gray-800` (9,01:1 / 10,47:1) na célula do código SIGTAP, que não é texto secundário —
        é o dado que sai na produção assinada pelo profissional. São tokens que **invertem** entre
        claro e escuro (`_variables.scss` x `_variables-dark.scss`), o que `text-muted` não faz o
        bastante. Travado por teste em `EncerramentoEsusModal.test.tsx`, no molde da trava de
        `EsusCadastroPacienteModal.test.tsx`.
      */}
      {temProcedimentos && (
        <>
          <div className='separator my-6' />

          <h5 className='mb-1'>Procedimentos</h5>
          <p className='text-gray-700 fs-7 mb-3'>
            Derivados das aferições deste atendimento. Vão na Ficha de Procedimentos enviada ao
            e-SUS APS <strong>em nome do profissional</strong> — não há o que preencher aqui.
          </p>

          <div className='table-responsive'>
            <table className='table table-row-dashed align-middle gy-2 mb-0'>
              <thead>
                <tr className='fw-semibold text-gray-700 fs-7'>
                  <th scope='col'>Procedimento</th>
                  <th scope='col'>Código</th>
                  <th scope='col' className='text-end'>
                    Quantidade
                  </th>
                </tr>
              </thead>
              <tbody>
                {procedimentos.map((procedimento) => (
                  <tr key={procedimento.codigo}>
                    <td>{procedimento.descricao}</td>
                    <td className='text-gray-800'>{procedimento.codigo}</td>
                    <td className='text-end'>{procedimento.quantidade}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className='d-flex flex-center flex-row-fluid pt-12'>
        <Button type={ButtonType.Reset} className='btn-light me-3' onClick={closeModal}>
          Cancelar
        </Button>
        <Button type={ButtonType.Submit} className='btn-primary'>
          Finalizar atendimento
        </Button>
      </div>
    </form>
  )
}
